import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';

import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { PayoutInvoiceInterface } from '~/organizer/payout-invoices/interfaces/payout-invoice.interface';
import { LoaderService } from '~/core/services/loader/loader.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import diacritic from 'diacritic';
import { mkConfig, generateCsv, download } from 'export-to-csv';

import { LocalizedDatePipe } from '~/core/pipes/localized-date.pipe';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { CurrencyPipe } from '@angular/common';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { ToastsService } from '~/notifications/toasts/toasts.service';

@Component({
  selector: 'app-payout-invoices',
  templateUrl: './payout-invoices.component.html',
  styleUrls: ['./payout-invoices.component.scss'],
})
export class PayoutInvoicesComponent implements OnDestroy, OnInit {
  private readonly subscriptions = new Subscription();

  private page = 1;
  private search = '';

  public readonly tipsVariant = TipsVariantEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;

  public hasApiError = false;
  public hasLoadMoreButton = false;
  public hasPagination = false;
  public isLoading = true;
  public payoutInvoices: PayoutInvoiceInterface[] = [];
  public formGroup: FormGroup;
  public firstActivityName: string;
  public asc = false;

  constructor(
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService,
    public readonly loaderService: LoaderService,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
    private readonly localizedDatePipe: LocalizedDatePipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly toastsService: ToastsService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.getOrganizationInvoices(false));

    this.formGroup = this.formBuilder.group({
      name: ['', Validators.required],
    });

    this.formGroup.controls['name'].valueChanges.subscribe((value) => {
      this.page = 1;
      this.search = value.toString();
      this.getOrganizationInvoices(false, this.search);
    });
  }

  public get placeholderInput(): string {
    return this.translateService.instant(
      'organizer_payout_invoices.search.placeholder',
      {
        firstActivityName: this.firstActivityName || '',
      }
    );
  }

  public getMoreInvoices(): void {
    this.page++;
    this.getOrganizationInvoices(true, this.search !== '' ? this.search : null);
  }

  private getOrganizationInvoices(loadMore: boolean, search?: string): void {
    this.organizerSharedOrganizationService
      .getOrganizationPayoutInvoices(
        this.organizerSharedOrganizationService.organization?.identifier,
        search,
        10,
        this.page
      )
      .subscribe({
        next: (payoutResponse) => {
          this.hasLoadMoreButton = payoutResponse?.remaining > 0;
          this.hasPagination = payoutResponse?.remaining > 0;
          this.payoutInvoices = loadMore
            ? this.payoutInvoices?.concat(payoutResponse?.items)
            : payoutResponse?.items;
          this.sortInvoices('createdAt', 'date');
          this.firstActivityName = this.payoutInvoices?.[0]?.activity?.name;
          this.loaderService.loading$.subscribe(() => {
            this.isLoading = false;
          });
        },
        error: () => {
          this.loaderService.loading$.subscribe(() => {
            this.isLoading = false;
            this.hasApiError = true;
          });
        },
      });
  }

  public tableExport(): void {
    this.toastsService.push({
      header: this.translateService.instant(
        'organizer_payout_invoices.export.toast.title'
      ),
      description: this.translateService.instant(
        'organizer_payout_invoices.export.toast.description'
      ),
      iconName: VtmnPlayIconTypeEnum.Information,
      variant: TipsVariantEnum.INFO,
    });

    const filenameCurrentDate = this.localizedDatePipe
      .transform(new Date(), 'shortDate')
      .replaceAll('/', '-');
    const filenameDedicated = `${diacritic.clean(this.organizerSharedOrganizationService.organization.name).replaceAll(' ', '-').toLowerCase()}_${filenameCurrentDate}`;

    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    let arrayForExport = [];
    const exportArray = [];
    arrayForExport = this.payoutInvoices.map(
      ({ feesUrl, apiIdentifier, attendeesUrl, ...arrayForExport }) =>
        arrayForExport
    );

    arrayForExport.forEach((item) => {
      const price = displayPricePipe.transform(
        item.amount?.amount,
        item.amount?.currency
      );
      const feesAmount = displayPricePipe.transform(
        item.feeAmount,
        item.amount?.currency
      );
      const paymentDate = this.localizedDatePipe.transform(
        item.createdAt?.date,
        'shortDate'
      );

      exportArray.push({
        'payment Date': paymentDate,
        'activity Date': item.activity?.date,
        'invoice identifier': item.invoiceNumber,
        amount: price,
        'fees amount': feesAmount,
        activity: item.activity?.name,
      });
    });

    const csvConfig = mkConfig({
      filename: filenameDedicated,
      showTitle: false,
      useKeysAsHeaders: true,
    });
    const csv = generateCsv(csvConfig)(exportArray);
    download(csvConfig)(csv);
  }

  public sortInvoicesByDate(
    invoicesArray: PayoutInvoiceInterface[]
  ): PayoutInvoiceInterface[] {
    if (this.asc) {
      return invoicesArray.sort(
        (a: PayoutInvoiceInterface, b: PayoutInvoiceInterface) =>
          b.createdAt?.date >= a.createdAt?.date ? 1 : -1
      );
    }
    return invoicesArray.sort(
      (a: PayoutInvoiceInterface, b: PayoutInvoiceInterface) =>
        a.createdAt?.date >= b.createdAt?.date ? 1 : -1
    );
  }

  public sortInvoices(
    firstParam: string,
    secondParam?: string
  ): PayoutInvoiceInterface[] {
    this.asc = !this.asc;
    return this.payoutInvoices.sort(
      (a: PayoutInvoiceInterface, b: PayoutInvoiceInterface) => {
        const currentItem = !this.asc ? b : a;
        const nextItem = !this.asc ? a : b;

        return currentItem[firstParam][secondParam] >=
          nextItem[firstParam][secondParam]
          ? 1
          : -1;
      }
    );
  }
}
